import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertTriangle } from 'lucide-react';
import SignInOptions from '../../components/SignInOptions';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// /wp-signin?org=<org id>&to=<path> — the club website's "Sign in without a password" (docs/OL_ACCESS_LINKS.md §12).
// Signed in to OL already (personal link, approved computer, email code) → straight back to the website,
// signed in. Otherwise: phone approval or emailed code first. OL builds the return address from the
// org's connected website — this page only passes the path.
export default function WpSignInPage() {
  const qs = new URLSearchParams(window.location.search);
  const org = qs.get('org') || '';
  const rawTo = qs.get('to') || '/';
  const to = /^\/(?![\/\\])/.test(rawTo) ? rawTo : '/';     // a path on the club site only
  const n = qs.get('n') || '';
  const [site, setSite] = useState(null);
  const [state, setState] = useState('checking');
  const [err, setErr] = useState('');

  const goBack = useCallback(async () => {
    setState('going');
    try {
      const { data } = await axios.post(`${API_URL}/api/access/wp-ticket`, { org_id: org, path: to, n }, { withCredentials: true });
      window.location.replace(data.url);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(e?.response?.status === 401 ? '' : ((d && (d.message || d)) || 'Could not sign in to the website'));
      setState('signin');
    }
  }, [org, to, n]);

  useEffect(() => {
    axios.get(`${API_URL}/api/access/site/${encodeURIComponent(org)}`).then(r => setSite(r.data))
      .catch(() => { setErr('This club website is not connected.'); setState('error'); });
  }, [org]);
  useEffect(() => { if (site) goBack(); }, [site, goBack]);

  const accent = site?.brand?.primary_color || '#d97706';
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4" data-testid="wp-signin-page">
      <div className="max-w-md w-full rounded-xl border bg-white dark:bg-slate-800 dark:border-slate-700 p-6 text-center space-y-4">
        {site?.brand?.logo_url && <img src={site.brand.logo_url.startsWith('http') ? site.brand.logo_url : `${API_URL}${site.brand.logo_url}`} alt="" className="h-14 mx-auto object-contain" />}
        <h1 className="text-xl font-bold">Sign in to {site?.brand?.org_name || 'the club website'}</h1>
        {state === 'error' && <p className="text-sm text-red-600 flex items-center justify-center gap-1"><AlertTriangle className="h-4 w-4" />{err}</p>}
        {(state === 'checking' || state === 'going') && <p className="text-sm text-slate-500 flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Signing you in…</p>}
        {state === 'signin' && (<>
          {err && <p className="text-sm text-amber-700" role="alert">{err}</p>}
          <p className="text-sm text-slate-500">No password needed.</p>
          <SignInOptions orgId={org} accent={accent} onSignedIn={goBack} />
          {site?.site_url && <a className="text-xs underline text-slate-500" href={new URL(to, site.site_url + '/').href}>Back to the website</a>}
        </>)}
      </div>
    </div>
  );
}
